import { AnimatePresence, motion } from 'framer-motion';
import { MapPin } from 'lucide-react';
import React, { Suspense } from 'react';
import Footer from '../../shared/components/Footer';
import Map from '../centered/components/Map';
import { LayoutContext } from '../../shared/hooks/useLayoutContext';
import { SplitScreenProvider, useSplitScreen } from './SplitScreenContext';

const SplitRightPanel: React.FC = () => {
  const { contextPanel } = useSplitScreen();

  return (
    <div className="lp-relative lp-w-full lp-h-full lp-overflow-hidden">
      <AnimatePresence mode="wait">
        {contextPanel?.type === 'location' ? (
          <motion.div
            key={`map-${contextPanel.data.id}`}
            className="lp-absolute lp-inset-0"
            initial={{ opacity: 0, scale: 1.03 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.98 }}
            transition={{ duration: 0.45, ease: 'easeInOut' }}
          >
            <Suspense
              fallback={
                <div className="lp-w-full lp-h-full lp-flex lp-items-center lp-justify-center" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
                  <MapPin className="lp-w-10 lp-h-10 lp-animate-pulse" style={{ color: 'rgba(221,184,137,0.3)' }} />
                </div>
              }
            >
              <Map
                position={{
                  lat: parseFloat(contextPanel.data.lat),
                  lng: parseFloat(contextPanel.data.long),
                }}
                onLocationChange={() => {}}
                disableInteraction={true}
              />
            </Suspense>

            {/* Location info overlay */}
            <motion.div
              className="lp-absolute lp-bottom-0 lp-left-0 lp-right-0 lp-px-10 lp-py-8"
              style={{ background: 'linear-gradient(to top, rgba(0,0,0,0.9) 0%, rgba(0,0,0,0.5) 50%, transparent 100%)' }}
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2, duration: 0.35 }}
            >
              <p className="lp-text-3xl lp-font-boska lp-tracking-wide" style={{ color: '#DDB889' }}>
                {contextPanel.data.city}
              </p>
              <p className="lp-text-sm lp-mt-1" style={{ color: '#9CA3AF' }}>
                {contextPanel.data.address}
              </p>
            </motion.div>
          </motion.div>
        ) : (
          <motion.div
            key="default"
            className="lp-absolute lp-inset-0 lp-flex lp-items-center lp-justify-center"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            style={{ background: 'radial-gradient(ellipse at 50% 40%, rgba(221,184,137,0.08) 0%, rgba(0,0,0,0) 70%)' }}
          >
            <MapPin style={{ width: 80, height: 80, color: 'rgba(221,184,137,0.12)' }} />
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

interface SplitScreenLayoutProps {
  children: React.ReactNode;
  backgroundColor?: string | null;
}

const SplitScreenLayout: React.FC<SplitScreenLayoutProps> = ({ children, backgroundColor }) => {
  const bgColor = backgroundColor || 'rgba(0, 0, 0, 0.95)';

  return (
    <LayoutContext.Provider value={{ layoutType: 'split-screen' }}>
      <SplitScreenProvider>
        <div
          className="lp-min-h-screen"
          style={{ backgroundColor: bgColor, display: 'grid', gridTemplateColumns: '60% 40%' }}
        >
          {/* Left panel — booking flow, always warm light */}
          <div
            className="lp-flex lp-flex-col lp-min-h-screen lp-overflow-y-auto"
            style={{ backgroundColor: '#F5F2EE', borderRight: '1px solid rgba(221,184,137,0.15)' }}
          >
            <div className="lp-flex-1 lp-py-12 lp-px-10">
              {children}
            </div>
            <Footer includeTop={false} />
          </div>

          {/* Right panel — sticky map */}
          <div className="lp-sticky lp-top-0 lp-h-screen">
            <SplitRightPanel />
          </div>
        </div>
      </SplitScreenProvider>
    </LayoutContext.Provider>
  );
};

export default SplitScreenLayout;
